import type { Theme, ThemeOptions } from '@mui/material/styles';
import { alpha, getContrastRatio, getOverlayAlpha, lighten } from '@mui/material/styles';
import type { Skin } from 'c-entity-types';

export type ThemeMode = 'light' | 'dark';

/**
 * Hidden reports (regular and custom) are rendered headless into PowerPoint slides and previewed in
 * an iframe, so they always use the light theme whatever the viewer has chosen.
 */
export const isHiddenReportPath = (pathname: string | null | undefined) =>
    pathname?.startsWith('/hidden-report') ?? false;

export const themeModeForPath = (mode: ThemeMode, pathname: string | null | undefined): ThemeMode =>
    isHiddenReportPath(pathname) ? 'light' : mode;

// the darkest surface brand colours are drawn on, so the one they need to contrast with
const darkModeSurface = '#1c1f26';

export const darkThemeOptions: ThemeOptions = {
    palette: {
        mode: 'dark',
        custom: {
            contentBgColor: '#121418',
            navigationBgColor: '#1a1d23',
        },
        background: {
            default: '#121418',
            paper: darkModeSurface,
        },
        secondary: {
            main: '#9e9e9e',
        },
        text: {
            primary: 'rgba(255, 255, 255, 0.92)',
            secondary: 'rgba(255, 255, 255, 0.65)',
            disabled: 'rgba(255, 255, 255, 0.4)',
        },
        divider: 'rgba(255, 255, 255, 0.12)',
    },
    components: {
        MuiTableCell: {
            styleOverrides: {
                // MUI's dark default is a solid grey which is very heavy at our 2px row dividers
                root: { borderBottomColor: 'rgba(255, 255, 255, 0.08)' },
            },
        },
    },
};

// WCAG AA for normal text, as primary is used for links and text as well as buttons
const minDarkModeContrast = 4.5;

/**
 * Lightens a colour in steps until it's readable on the dark mode surfaces. Colours that are already
 * readable are returned untouched so most brands look exactly as they do in light mode.
 */
export const readableOnDark = (color: string) => {
    if (color == null || color.trim().length === 0) {
        return color;
    }

    try {
        let adjusted = color;
        // small steps so we stop as close to the original colour as possible
        for (let step = 1; step <= 20; step += 1) {
            if (getContrastRatio(adjusted, darkModeSurface) >= minDarkModeContrast) {
                return adjusted;
            }
            adjusted = lighten(color, step / 20);
        }
        return adjusted;
    } catch (e) {
        // MUI throws on colour strings it can't parse, leave those as they were
        return color;
    }
};

const skinBrandColors: { palette: string; light: keyof Skin; dark: keyof Skin }[] = [
    { palette: 'primary', light: 'primary_color', dark: 'dark_primary_color' },
    { palette: 'secondary', light: 'secondary_color', dark: 'dark_secondary_color' },
    { palette: 'tertiary', light: 'third_color', dark: 'dark_third_color' },
    { palette: 'quaternary', light: 'fourth_color', dark: 'dark_fourth_color' },
    { palette: 'quinary', light: 'fifth_color', dark: 'dark_fifth_color' },
    { palette: 'senary', light: 'sixth_color', dark: 'dark_sixth_color' },
];

/**
 * Skins only define light surface/text colours, so in dark mode we keep just their brand colours
 * and let the dark palette provide the rest. Brand colours are either adjusted automatically to be
 * readable, or taken from the skin's manual dark colours (falling back to the regular colour) when
 * the skin has opted out of automatic adjustment.
 */
export const skinThemeForMode = (
    { theme, skin }: { theme: ThemeOptions; skin?: Skin },
    mode: ThemeMode,
): ThemeOptions => {
    if (mode !== 'dark' || theme?.palette == null || skin == null) {
        return theme;
    }

    // null until the column exists/is set, which counts as automatic
    const automatic = skin.dark_mode_auto !== false;
    const palette = {};
    skinBrandColors.forEach(({ palette: paletteKey, light, dark }) => {
        const lightColor = skin[light] as string;
        const darkColor = skin[dark] as string;
        const color = automatic ? readableOnDark(lightColor) : darkColor || lightColor;

        if (color) {
            palette[paletteKey] = { main: color };
        }
    });

    return { ...theme, palette };
};

/**
 * Recharts hardcodes its default tooltip and bar hover band as inline white/#ccc, so in dark mode
 * they're overridden globally rather than chart by chart. Custom tooltips use Paper and follow the
 * theme already.
 */
export const rechartsDarkModeCss = (theme: Theme) => `
    .recharts-default-tooltip {
        background-color: ${theme.palette.background.paper} !important;
        border-color: ${theme.palette.divider} !important;
    }
    .recharts-rectangle.recharts-tooltip-cursor {
        fill: rgba(255, 255, 255, 0.06);
    }
`;

// background for form fields which used to be hardcoded to white
export const fieldBackdropColor = (theme: Theme) =>
    theme.palette.mode === 'dark' ? theme.palette.background.paper : theme.palette.common.white;

// background for form fields which used to be hardcoded to grey.200
export const greyFieldBackdropColor = (theme: Theme) =>
    theme.palette.mode === 'dark' ? theme.palette.grey[800] : theme.palette.grey[200];

/**
 * The colour of a Paper at `elevation`, for things that must sit opaque on top of it (sticky table
 * headers). Dark mode Paper isn't just background.paper: MUI lightens it with a white overlay that
 * grows with elevation (a Dialog is 24), so a plain background.paper reads as a dark block on it.
 */
export const paperSurface = (theme: Theme, elevation: number) => {
    const overlay = alpha('#fff', Number(getOverlayAlpha(elevation)));
    return {
        backgroundColor: theme.palette.background.paper,
        backgroundImage:
            theme.palette.mode === 'dark' ? `linear-gradient(${overlay}, ${overlay})` : 'none',
    };
};
